import { format } from 'date-fns';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from 'recharts';

import { type OutreachUrgencyFilter } from '@apps/web/components/admin/outreach_observability/constants';
import { Badge } from '@apps/web/components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import { cn } from '@apps/web/lib/utils';
import { useAdminAgentRunDurationTrends } from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    type AgentRunDurationTrendBucketRead,
    type Mode,
} from '@libraries/typescript/api_client';

type Granularity = 'hour' | 'day';

type RunTypeFilter =
    | 'all'
    | 'outreach_analysis'
    | 'general'
    | 'automation'
    | 'subagent'
    | 'internal'
    | 'admin_playground';

type RunTypeOption = {
    readonly label: string;
    readonly value: RunTypeFilter;
};

// Presets pair a granularity with a sensible lookback: recent hourly buckets
// for real-time debugging, or a longer daily view for slower trends.
const GRANULARITY_PRESETS: Record<
    Granularity,
    { readonly label: string; readonly windowHours: number }
> = {
    hour: { label: 'Hourly · last 48h', windowHours: 48 },
    day: { label: 'Daily · last 14d', windowHours: 14 * 24 },
};

const RUN_TYPE_OPTIONS: Array<RunTypeOption> = [
    { label: 'All run types', value: 'all' },
    { label: 'Outreach analysis', value: 'outreach_analysis' },
    { label: 'General', value: 'general' },
    { label: 'Automation', value: 'automation' },
    { label: 'Subagent', value: 'subagent' },
    { label: 'Internal', value: 'internal' },
    { label: 'Admin playground', value: 'admin_playground' },
];

function formatDurationMs(durationMs: number | null): string {
    if (durationMs === null) {
        return '—';
    }
    const seconds = durationMs / 1000;
    if (seconds < 60) {
        return `${seconds.toFixed(1)}s`;
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
}

function formatAgeSeconds(ageS: number | null): string {
    if (ageS === null) {
        return '—';
    }
    return formatDurationMs(ageS * 1000);
}

type MetricKey = 'duration' | 'first_gen';

type ChartPoint = {
    readonly label: string;
    readonly avg: number | null;
    readonly p95: number | null;
    readonly max: number | null;
    readonly runCount: number;
};

const SERIES: Array<{
    readonly key: 'avg' | 'p95' | 'max';
    readonly label: string;
    readonly color: string;
}> = [
    { key: 'avg', label: 'Average', color: 'var(--chart-2)' },
    { key: 'p95', label: 'p95', color: 'var(--chart-4)' },
    { key: 'max', label: 'Max', color: 'var(--destructive)' },
];

function toChartPoint(
    bucket: AgentRunDurationTrendBucketRead,
    granularity: Granularity,
    metric: MetricKey,
): ChartPoint {
    const labelFormat = granularity === 'hour' ? 'M/d HH:00' : 'M/d';
    const avg =
        metric === 'duration'
            ? bucket.avg_duration_ms
            : bucket.avg_first_gen_latency_ms;
    const p95 =
        metric === 'duration'
            ? bucket.p95_duration_ms
            : bucket.p95_first_gen_latency_ms;
    const max =
        metric === 'duration'
            ? bucket.max_duration_ms
            : bucket.max_first_gen_latency_ms;
    return {
        label: format(new Date(bucket.bucket_start), labelFormat),
        avg,
        p95,
        max,
        runCount: bucket.measured_run_count,
    };
}

type DurationTooltipEntry = {
    readonly name?: string;
    readonly value?: number | null;
    readonly color?: string;
};

function DurationTooltip({
    active,
    payload,
    label,
}: {
    readonly active?: boolean;
    readonly payload?: Array<DurationTooltipEntry>;
    readonly label?: string;
}) {
    if (!active || !payload || payload.length === 0) {
        return null;
    }
    return (
        <div className="bg-background rounded-md border px-3 py-2 text-xs shadow-sm">
            <div className="mb-1 font-medium">{label}</div>
            {payload.map((entry) => (
                <div
                    key={entry.name}
                    className="flex items-center justify-between gap-4"
                >
                    <span style={{ color: entry.color }}>{entry.name}</span>
                    <span className="tabular-nums">
                        {formatDurationMs(entry.value ?? null)}
                    </span>
                </div>
            ))}
        </div>
    );
}

function DurationLineChart({
    buckets,
    granularity,
    metric,
    title,
}: {
    readonly buckets: Array<AgentRunDurationTrendBucketRead>;
    readonly granularity: Granularity;
    readonly metric: MetricKey;
    readonly title: string;
}) {
    const data = buckets.map((bucket) =>
        toChartPoint(bucket, granularity, metric),
    );
    return (
        <div className="flex flex-1 flex-col gap-2 rounded-md border p-3">
            <div className="text-sm font-medium">{title}</div>
            {data.length === 0 ? (
                <div className="text-muted-foreground flex h-64 items-center justify-center text-sm">
                    No completed runs in this window.
                </div>
            ) : (
                <div className="h-64 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={data} margin={{ left: 8, right: 8 }}>
                            <CartesianGrid
                                stroke="var(--border)"
                                strokeDasharray="3 3"
                            />
                            <XAxis
                                dataKey="label"
                                tick={{ fontSize: 11 }}
                                minTickGap={24}
                            />
                            <YAxis
                                tick={{ fontSize: 11 }}
                                width={48}
                                tickFormatter={(value: number) =>
                                    formatDurationMs(value)
                                }
                            />
                            <RechartsTooltip content={<DurationTooltip />} />
                            {SERIES.map((series) => (
                                <Line
                                    key={series.key}
                                    type="monotone"
                                    dataKey={series.key}
                                    name={series.label}
                                    stroke={series.color}
                                    dot={false}
                                    strokeWidth={2}
                                    connectNulls={true}
                                    isAnimationActive={false}
                                />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    );
}

type AgentRunDurationTrendPanelProps = {
    // Initial run-type selection. The selector stays visible and switchable;
    // this only seeds its default (e.g. outreach observability defaults to
    // outreach_analysis but can be changed).
    readonly defaultRunType?: RunTypeFilter;
    // When set, scopes the trends to these organizations (mirrors a page-level
    // org filter). Omit for an admin-wide view.
    readonly organizationIds?: Array<string>;
    // When set, scopes the trends to a single mode. Outreach observability
    // passes 'live' so these charts match the rest of that page, which is
    // computed under live mode only. Omit to aggregate across modes.
    readonly mode?: Mode;
    readonly urgency?: OutreachUrgencyFilter;
};

export function AgentRunDurationTrendPanel({
    defaultRunType = 'all',
    organizationIds,
    mode,
    urgency,
}: AgentRunDurationTrendPanelProps = {}) {
    const [granularity, setGranularity] = useState<Granularity>('hour');
    const [runType, setRunType] = useState<RunTypeFilter>(defaultRunType);

    const queryParams = useMemo(
        () => ({
            granularity,
            window_hours: GRANULARITY_PRESETS[granularity].windowHours,
            ...(runType === 'all' ? {} : { run_type: runType }),
            ...(organizationIds && organizationIds.length > 0
                ? { organization_id: organizationIds }
                : {}),
            ...(mode ? { mode } : {}),
            ...(urgency ? { urgency } : {}),
        }),
        [granularity, runType, organizationIds, mode, urgency],
    );

    const { data, isLoading, error } =
        useAdminAgentRunDurationTrends(queryParams);

    const inFlight = data?.in_flight;
    const hasStuckRuns =
        inFlight !== undefined && inFlight.long_running_count > 0;

    return (
        <div className="flex flex-col gap-3 rounded-md border p-4">
            <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-sm font-semibold">Run duration trends</h2>
                {isLoading && (
                    <Loader2 className="text-muted-foreground h-3 w-3 animate-spin" />
                )}
                <div className="ml-auto flex items-center gap-2">
                    <Select
                        value={runType}
                        onValueChange={(value) =>
                            setRunType(value as RunTypeFilter)
                        }
                    >
                        <SelectTrigger className="h-8 w-fit gap-1 text-xs">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {RUN_TYPE_OPTIONS.map((option) => (
                                <SelectItem
                                    key={option.value}
                                    value={option.value}
                                >
                                    {option.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Select
                        value={granularity}
                        onValueChange={(value) =>
                            setGranularity(value as Granularity)
                        }
                    >
                        <SelectTrigger className="h-8 w-fit gap-1 text-xs">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {(
                                Object.keys(
                                    GRANULARITY_PRESETS,
                                ) as Array<Granularity>
                            ).map((key) => (
                                <SelectItem key={key} value={key}>
                                    {GRANULARITY_PRESETS[key].label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>
            {inFlight !== undefined && (
                <div className="flex flex-wrap items-center gap-2 text-xs">
                    <Badge variant="secondary">
                        {inFlight.running_count} running now
                    </Badge>
                    <Badge
                        variant={hasStuckRuns ? 'destructive' : 'secondary'}
                        className={cn(
                            'flex items-center gap-1',
                            hasStuckRuns ? '' : 'opacity-80',
                        )}
                    >
                        {hasStuckRuns && <AlertTriangle className="h-3 w-3" />}
                        {inFlight.long_running_count} running &gt;
                        {Math.round(inFlight.long_running_threshold_s / 60)}m
                    </Badge>
                    <span className="text-muted-foreground">
                        Oldest in-flight run:{' '}
                        {formatAgeSeconds(inFlight.oldest_running_age_s)}
                    </span>
                </div>
            )}
            {error ? (
                <div className="text-destructive flex h-64 items-center justify-center text-sm">
                    Failed to load duration trends.
                </div>
            ) : (
                <div className="flex flex-col gap-3 lg:flex-row">
                    <DurationLineChart
                        buckets={data?.buckets ?? []}
                        granularity={granularity}
                        metric="duration"
                        title="Total run duration"
                    />
                    <DurationLineChart
                        buckets={data?.buckets ?? []}
                        granularity={granularity}
                        metric="first_gen"
                        title="First gen latency"
                    />
                </div>
            )}
        </div>
    );
}
